<script setup>
import { ref, computed, onBeforeMount } from 'vue'
import { useUsersStore, useRestaurantsStore } from '@/stores'
import googleAttribution from '@/assets/images/ui/google_on_white.png'
import MemberFavorites from "@core/components/MemberFavorites.vue"

// Store initialization
const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()

// Store refs
const myUser = computed(() => usersStore.myUser)
const restaurants = computed(() => restaurantsStore.restaurants)

// Loading state
const isLoading = ref(true)

// Founder review, 2026-09-17: Favorites → Notes is where a note about a restaurant the
// member did NOT favorite lives, and RM AI links them straight here. A member with notes
// and no Favorites must not be shown the "favorite something" empty state.
const hasAnythingToShow = computed(() => !!(
  myUser.value?.favoritedRestaurants?.length || restaurantsStore.unfavoritedNotes?.length
))

// Computed property
const myFavoritesCount = computed(() =>
  restaurants.value.filter(r =>
    r.isFavorited ||
    r.hasOwnProperty('unfavorited')
  ).length
)

// Lifecycle hooks
onBeforeMount(async () => {
  // Fetch fresh user data and restaurants in parallel
  await Promise.all([
    usersStore.getMyUser(),
    restaurantsStore.getRestaurants(),

    // Needed before the empty state decides: a member can have notes and no Favorites.
    restaurantsStore.refreshUnfavoritedNotes(),
  ])
  isLoading.value = false
})
</script>

<template>
  <div>
    <!-- Main Content -->
    <div class="main-wrapper">
      <div class="max-content-width position-relative">
        <!-- Favorites Content -->
        <div class="pb-3">
          <!-- Loading State -->
          <div
            v-if="isLoading"
            class="upcoming-empty-state content-card ma-1 mt-4"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="40"
            />
          </div>
          <!-- Empty State -->
          <div
            v-else-if="!hasAnythingToShow"
            class="upcoming-empty-state content-card ma-1 mt-4"
          >
            <div class="text-sm">
              {{ $t('favorites.emptyState') }}
            </div>
          </div>
          <!-- Favorites List -->
          <template v-else>
            <MemberFavorites :member="myUser" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-wrapper {
  width: 100%;
  margin: 0 auto;
  padding: 0 0.4rem;
}

/* Shared ~1160px container, matching Discover + the navbar contents */
.max-content-width {
  max-width: 1160px;
  padding: 0 12px;
}

@media (min-width: 960px) {
  .max-content-width {
    padding: 0 32px;
  }
}

.upcoming-empty-state {
  min-height: 36vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.google-attribution {
  width: fit-content;
  margin-bottom: 1rem;
  margin-left: 0.4rem;
}

:deep(.v-btn--active) {
  border-bottom: 1px solid;
}

:deep(.v-field__input) {
  font-size: 16px !important;
}

:deep(.v-autocomplete .v-autocomplete__selection-text) {
  overflow: unset !important;
}

:deep(.v-table > .v-table__wrapper > table > tbody > tr > td) {
  padding: 0 !important;
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  font-weight: bold;
  padding: 0 !important;
}

:deep(.outer-table > .v-table__wrapper > table) {
  padding-left: 4px !important;
}

:deep(.inner-table > .v-table__wrapper > table) {
  padding-left: 2px !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
